import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Home, Plus, Users, Bed, MapPin, AlertTriangle,
  FileText, ChevronRight, Camera,
  UserPlus, Activity, CheckCircle2,
  MessageSquare, FlaskConical, Moon, ListChecks, BarChart3, Trash2,
  User, Upload, X, Edit, Save, Phone, Mail, BookOpen, Image,
  DollarSign, CalendarCheck, ClipboardCheck
} from 'lucide-react';
import { OurSquareLogo } from '../../components/brand/Logo';

const ENTITY = 'our_square';

export default function OurSquarePage({ subpage }) {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const isCoordinator = user.role === 'housing_coordinator';
  const isStaff = ['housing_coordinator', 'housing_staff', 'coordinator', 'admin'].includes(user.role);

  const page = subpage || 'dashboard';

  return (
    <div className="space-y-6" data-testid="our-square-page">
      <div className="flex items-center gap-4">
        <OurSquareLogo size="sm" className="h-16 w-auto" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Our Square Women's Housing
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Transitional Living &amp; Sober Living for Women in Recovery</p>
        </div>
      </div>

      {page === 'dashboard' && <OSDashboard api={api} isStaff={isStaff} isCoordinator={isCoordinator} />}
      {page === 'properties' && <OSProperties api={api} isStaff={isStaff} isCoordinator={isCoordinator} />}
      {page === 'residents' && <OSResidents api={api} isStaff={isStaff} />}
      {page === 'meetings' && <OSMeetings api={api} isStaff={isStaff} />}
      {page === 'incidents' && <OSIncidents api={api} isStaff={isStaff} />}
      {page === 'waitlist' && <OSWaitlist api={api} isStaff={isStaff} />}
      {page === 'rent' && <OSRentTracker api={api} isStaff={isStaff} isCoordinator={isCoordinator} />}
      {page === 'reports' && <OSReports api={api} />}
      {page === 'referrals' && <OSReferrals api={api} />}
    </div>
  );
}

/* =============================== DASHBOARD =============================== */
function OSDashboard({ api, isStaff, isCoordinator }) {
  const [data, setData] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [fullHouses, setFullHouses] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);

  useEffect(() => { api.get(`/housing/entity/${ENTITY}/dashboard`).then(r => setData(r.data)).catch(() => {}); }, []);

  const openDrill = (type) => {
    if (['occupied', 'available', 'beds'].includes(type)) {
      api.get(`/housing/entity/${ENTITY}/houses`).then(r => { setFullHouses(r.data); setDrilldown(type); }).catch(() => {});
    } else if (type === 'properties') {
      window.location.hash = '';
      window.location.href = '/our-square/properties';
    } else if (type === 'waitlist') {
      window.location.href = '/our-square/waitlist';
    } else if (type === 'rent') {
      window.location.href = '/our-square/rent';
    } else {
      setDrilldown(null);
    }
  };

  // If a resident is selected, show their profile
  if (selectedResident) {
    return <OSResidentProfile api={api} residentId={selectedResident} isStaff={isStaff} onBack={() => setSelectedResident(null)} />;
  }

  if (!data) return <div className="text-center py-12 text-muted-foreground">Loading dashboard...</div>;

  const stats = [
    { key: 'properties', label: 'Properties', value: data.total_properties, icon: Home, color: 'bg-cyan-50 text-cyan-700', hover: 'hover:ring-2 hover:ring-cyan-200' },
    { key: 'beds', label: 'Total Beds', value: data.total_beds, icon: Bed, color: 'bg-blue-50 text-blue-700', hover: 'hover:ring-2 hover:ring-blue-200' },
    { key: 'occupied', label: 'Occupied', value: data.occupied_beds, icon: Users, color: 'bg-amber-50 text-amber-700', hover: 'hover:ring-2 hover:ring-amber-200' },
    { key: 'available', label: 'Available', value: data.available_beds, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700', hover: 'hover:ring-2 hover:ring-emerald-200' },
    { key: 'waitlist', label: 'Waitlist', value: data.waitlist_count, icon: ListChecks, color: 'bg-purple-50 text-purple-700', hover: 'hover:ring-2 hover:ring-purple-200' },
    { key: 'rent', label: 'Rent (this month)', value: `$${data.rent_collected_this_month || 0}`, icon: DollarSign, color: 'bg-green-50 text-green-700', hover: 'hover:ring-2 hover:ring-green-200' },
  ];

  return (
    <div className="space-y-6" data-testid="os-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map(s => (
          <Card key={s.key} className={`border-0 shadow-sm rounded-xl cursor-pointer transition-all ${s.hover}`} onClick={() => openDrill(s.key)} data-testid={`os-stat-${s.key}`}>
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mb-2`}><s.icon size={18} /></div>
              <p className="text-2xl font-bold text-slate-900">{s.value}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">{s.label} <ChevronRight size={10} /></p>
            </CardContent>
          </Card>
        ))}
      </div>

      {drilldown && (
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-cyan-400" data-testid="os-drilldown">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              {drilldown === 'beds' && 'All Beds by Property'}
              {drilldown === 'occupied' && 'Occupied Beds — Current Residents'}
              {drilldown === 'available' && 'Available Beds by Property'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setDrilldown(null)} className="h-7"><X size={14} /></Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {fullHouses.map(h => (
                <div key={h.id} className="rounded-lg border border-slate-100 overflow-hidden">
                  <div className="flex items-center justify-between p-3 bg-slate-50">
                    <div className="flex items-center gap-2">
                      <Home size={16} className="text-cyan-600" />
                      <span className="font-medium text-sm">{h.name}</span>
                      <Badge variant="outline" className="text-[10px]">{h.housing_type === 'independent_sober' ? 'Sober Living' : 'Transitional'}</Badge>
                    </div>
                    <Badge className={drilldown === 'occupied' ? 'bg-amber-50 text-amber-700 border-0' : drilldown === 'available' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-blue-50 text-blue-700 border-0'}>
                      {drilldown === 'occupied' && `${h.current_residents} occupied`}
                      {drilldown === 'available' && `${h.beds_available} available`}
                      {drilldown === 'beds' && `${h.capacity} beds`}
                    </Badge>
                  </div>
                  {drilldown === 'occupied' && h.residents?.length > 0 && (
                    <div className="p-2 space-y-1">
                      {h.residents.map(r => (
                        <div key={r.id} className="flex items-center justify-between px-3 py-2 rounded bg-white text-xs cursor-pointer hover:bg-cyan-50 transition-colors" onClick={() => setSelectedResident(r.id)} data-testid={`dashboard-resident-${r.id}`}>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center text-[10px] font-bold text-cyan-700">{r.user_name?.charAt(0)}</div>
                            <span className="font-medium">{r.user_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">{r.program_phase}</Badge>
                            <Badge className={r.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0 text-[10px]' : 'bg-red-50 text-red-700 border-0 text-[10px]'}>{r.compliance_status || 'compliant'}</Badge>
                            <ChevronRight size={12} className="text-muted-foreground" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {drilldown === 'occupied' && (!h.residents || h.residents.length === 0) && <p className="text-xs text-muted-foreground px-3 py-2">No active residents</p>}
                  {drilldown === 'available' && <p className={`text-xs px-3 py-2 ${h.beds_available > 0 ? 'text-emerald-700' : 'text-red-500'}`}>{h.beds_available > 0 ? `${h.beds_available} bed${h.beds_available !== 1 ? 's' : ''} available` : 'Full — no beds available'}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:ring-2 hover:ring-cyan-200 transition-all" onClick={() => openDrill('occupied')}>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Occupancy Rate <ChevronRight size={12} className="text-muted-foreground" /></CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-2"><Progress value={data.occupancy_rate} className="h-3 flex-1" /><span className="text-lg font-bold">{data.occupancy_rate}%</span></div>
            <p className="text-xs text-muted-foreground">{data.occupied_beds} of {data.total_beds} beds occupied</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:ring-2 hover:ring-cyan-200 transition-all" onClick={() => openDrill('occupied')}>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">Compliance Status <ChevronRight size={12} className="text-muted-foreground" /></CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-emerald-500" /><span className="text-sm"><strong>{data.compliant_residents}</strong> Compliant</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500" /><span className="text-sm"><strong>{data.non_compliant_residents}</strong> Non-compliant</span></div>
            </div>
            {data.active_residents > 0 && <Progress value={(data.compliant_residents / data.active_residents) * 100} className="h-2 mt-3" />}
          </CardContent>
        </Card>
      </div>

      {data.houses.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-base">Our Square Properties</CardTitle></CardHeader>
          <CardContent><div className="space-y-2">
            {data.houses.map(h => (
              <div key={h.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-cyan-50 cursor-pointer transition-colors" onClick={() => window.location.href = '/our-square/properties'}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center"><Home size={16} className="text-cyan-700" /></div>
                  <div>
                    <p className="text-sm font-medium">{h.name}</p>
                    <div className="flex gap-1 mt-0.5">
                      <Badge variant="outline" className="text-[10px]">{h.housing_type === 'independent_sober' ? 'Sober Living' : 'Transitional'}</Badge>
                      <Badge variant="outline" className="text-[10px]">{h.gender_designation}</Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={h.occupied_beds < h.total_beds ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{h.total_beds - h.occupied_beds} available</Badge>
                  <span className="text-xs text-muted-foreground">{h.occupied_beds}/{h.total_beds}</span>
                </div>
              </div>
            ))}
          </div></CardContent>
        </Card>
      )}
    </div>
  );
}

/* =============================== PROPERTIES =============================== */
function OSProperties({ api, isStaff, isCoordinator }) {
  const [houses, setHouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: '', address: '', capacity: 4, description: '', gender_designation: 'men', program_type: 'transitional', housing_type: 'transitional', entity: ENTITY });

  const load = useCallback(() => {
    api.get(`/housing/entity/${ENTITY}/houses`).then(r => setHouses(r.data)).catch(() => {});
    if (isStaff) api.get('/housing/staff').then(r => setStaff(r.data)).catch(() => {});
  }, [api, isStaff]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name) return toast.error('Name required');
    try { await api.post('/housing/houses', form); toast.success('Property created!'); setOpen(false); setForm({ name: '', address: '', capacity: 4, description: '', gender_designation: 'men', program_type: 'transitional', housing_type: 'transitional', entity: ENTITY }); load(); }
    catch { toast.error('Failed'); }
  };

  if (selected) return <OSPropertyDetail api={api} houseId={selected} isStaff={isStaff} isCoordinator={isCoordinator} staff={staff} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="space-y-4" data-testid="os-properties">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{houses.length} properties</p>
        {isCoordinator && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2" data-testid="os-add-property"><Plus size={16} /> Add Property</Button></DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg">
              <DialogHeader><DialogTitle>Add Our Square Property</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
                <div className="space-y-1.5"><Label>Property Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Our Square - Women's House" /></div>
                <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Beds</Label><Input type="number" value={form.capacity} onChange={e => setForm({...form, capacity: parseInt(e.target.value) || 1})} /></div>
                  <div className="space-y-1.5"><Label>Gender</Label>
                    <Select value={form.gender_designation} onValueChange={v => setForm({...form, gender_designation: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="men">Men</SelectItem><SelectItem value="women">Women</SelectItem><SelectItem value="family">Family</SelectItem><SelectItem value="co-ed">Co-Ed</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Housing Type</Label>
                  <Select value={form.housing_type} onValueChange={v => setForm({...form, housing_type: v, program_type: v === 'independent_sober' ? 'sober_living' : 'transitional'})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transitional">Transitional Living</SelectItem>
                      <SelectItem value="independent_sober">Independent / Sober Living</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Description</Label><DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} rows={2} showCleanup={false} /></div>
                <Button onClick={create} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Create Property</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {houses.map(h => (
          <Card key={h.id} className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelected(h.id)} data-testid={`os-property-${h.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-50 flex items-center justify-center"><Home className="w-5 h-5 text-cyan-600" /></div>
                <Badge className={h.beds_available > 0 ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{h.beds_available} available</Badge>
              </div>
              <h3 className="font-semibold text-slate-900">{h.name}</h3>
              {h.address && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={10} />{h.address}</p>}
              <div className="flex items-center gap-2 mt-3">
                <Badge variant="outline" className="text-[10px]">{h.housing_type === 'independent_sober' ? 'Sober Living' : 'Transitional'}</Badge>
                <Badge variant="outline" className="text-[10px]">{h.gender_designation}</Badge>
                <span className="text-xs text-muted-foreground ml-auto">{h.current_residents}/{h.capacity} residents</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {houses.length === 0 && <Card className="col-span-full border-0 shadow-sm rounded-xl"><CardContent className="p-8 text-center text-muted-foreground"><Home className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p>No Our Square properties yet.</p></CardContent></Card>}
      </div>
    </div>
  );
}

/* =============================== PROPERTY DETAIL (condensed) =============================== */
function OSPropertyDetail({ api, houseId, isStaff, isCoordinator, staff, onBack }) {
  const [house, setHouse] = useState(null);
  const [clients, setClients] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [addResOpen, setAddResOpen] = useState(false);
  const [bulkCurfewOpen, setBulkCurfewOpen] = useState(false);
  const [resForm, setResForm] = useState({ user_id: '', move_in_date: '', program_phase: 'orientation', rent_amount: 0, assigned_peer_mentor_id: '' });
  const [curfewEntries, setCurfewEntries] = useState([]);
  const [curfewDate, setCurfewDate] = useState('');
  const [curfewTime, setCurfewTime] = useState('22:00');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const load = useCallback(() => {
    api.get(`/housing/houses/${houseId}`).then(r => {
      setHouse(r.data);
      setEditForm({ name: r.data.name||'', address: r.data.address||'', description: r.data.description||'', rules: r.data.rules||'', owner_name: r.data.owner_name||'', owner_bio: r.data.owner_bio||'', operator_name: r.data.operator_name||'', operator_phone: r.data.operator_phone||'', operator_email: r.data.operator_email||'', gender_designation: r.data.gender_designation||'men', intake_status: r.data.intake_status||'open' });
      // Initialize bulk curfew
      const residents = r.data.residents || [];
      setCurfewEntries(residents.map(re => ({ resident_id: re.id, user_id: re.user_id, user_name: re.user_name, status: 'on_time', check_in_time: '', notes: '' })));
    }).catch(() => {});
    if (isStaff) api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
  }, [api, houseId, isStaff]);
  useEffect(() => { load(); }, [load]);

  const saveEdits = async () => { try { await api.put(`/housing/houses/${houseId}`, editForm); toast.success('Saved!'); setEditMode(false); load(); } catch { toast.error('Failed'); } };
  const uploadPhoto = async (e) => { const file = e.target.files?.[0]; if (!file) return; setUploading(true); try { const fd = new FormData(); fd.append('file', file); await api.post(`/housing/houses/${houseId}/photos`, fd, { headers: {'Content-Type': 'multipart/form-data'}}); toast.success('Photo uploaded!'); load(); } catch { toast.error('Failed'); } setUploading(false); };
  const addResident = async () => { if (!resForm.user_id) return toast.error('Select participant'); try { await api.post('/housing/residents', { house_id: houseId, ...resForm }); toast.success('Resident added!'); setAddResOpen(false); load(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const exitResident = async (id) => { try { await api.put(`/housing/residents/${id}/exit?exit_reason=program_completion`); toast.success('Exited'); load(); } catch { toast.error('Failed'); } };
  const submitBulkCurfew = async () => {
    if (!curfewDate) return toast.error('Select date');
    try {
      await api.post('/housing/bulk-curfew', { house_id: houseId, date: curfewDate, curfew_time: curfewTime, entries: curfewEntries });
      toast.success(`${curfewEntries.length} curfew logs created!`);
      setBulkCurfewOpen(false);
    } catch { toast.error('Failed'); }
  };

  if (!house) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="os-property-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full">&larr; Back</Button>
          <div><h2 className="text-xl font-bold text-slate-900">{house.name}</h2>{house.address && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={10} />{house.address}</p>}</div>
        </div>
        <div className="flex gap-2">
          {isStaff && (
            <Dialog open={bulkCurfewOpen} onOpenChange={setBulkCurfewOpen}>
              <DialogTrigger asChild><Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full gap-1" data-testid="bulk-curfew-btn"><Moon size={14} /> Bulk Curfew</Button></DialogTrigger>
              <DialogContent className="rounded-2xl max-w-lg">
                <DialogHeader><DialogTitle>Bulk Curfew Check-in — {house.name}</DialogTitle></DialogHeader>
                <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={curfewDate} onChange={e => setCurfewDate(e.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Curfew Time</Label><Input type="time" value={curfewTime} onChange={e => setCurfewTime(e.target.value)} /></div>
                  </div>
                  <div className="space-y-2">
                    {curfewEntries.map((entry, idx) => (
                      <div key={entry.resident_id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                        <div className="w-7 h-7 rounded-full bg-cyan-100 flex items-center justify-center text-[10px] font-bold text-cyan-700">{entry.user_name?.charAt(0)}</div>
                        <span className="text-sm font-medium flex-1">{entry.user_name}</span>
                        <Select value={entry.status} onValueChange={v => { const copy = [...curfewEntries]; copy[idx].status = v; setCurfewEntries(copy); }}>
                          <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="on_time">On Time</SelectItem><SelectItem value="late">Late</SelectItem><SelectItem value="missed">Missed</SelectItem><SelectItem value="excused">Excused</SelectItem></SelectContent>
                        </Select>
                      </div>
                    ))}
                    {curfewEntries.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No active residents</p>}
                  </div>
                  <Button onClick={submitBulkCurfew} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="submit-bulk-curfew">Check In All ({curfewEntries.length})</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
          {isCoordinator && (editMode ? (
            <><Button size="sm" onClick={saveEdits} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-1"><Save size={14} /> Save</Button><Button size="sm" variant="outline" onClick={() => setEditMode(false)} className="rounded-full">Cancel</Button></>
          ) : <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className="rounded-full gap-1"><Edit size={14} /> Edit</Button>)}
        </div>
      </div>

      {/* Photos */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><Image size={14} /> Photos</CardTitle>
          {isCoordinator && <><input type="file" ref={fileInput} accept="image/*" className="hidden" onChange={uploadPhoto} /><Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" onClick={() => fileInput.current?.click()} disabled={uploading}><Upload size={12} /> {uploading ? 'Uploading...' : 'Upload'}</Button></>}
        </CardHeader>
        <CardContent>
          {house.photos?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{house.photos.map((p,i) => <div key={`item-${i}`} className="relative group rounded-xl overflow-hidden aspect-video bg-slate-100"><img src={`${backendUrl}${p}`} alt="" className="w-full h-full object-cover" /></div>)}</div>
          ) : <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-xl"><Camera size={24} className="mx-auto mb-2 text-slate-300" /><p className="text-xs">No photos yet</p></div>}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{house.total_beds || house.capacity}</p><p className="text-xs text-muted-foreground">Beds</p></CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-emerald-600">{house.beds_available ?? house.available_beds ?? 0}</p><p className="text-xs text-muted-foreground">Available</p></CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-600">{house.current_residents}</p><p className="text-xs text-muted-foreground">Residents</p></CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center"><Badge className={house.intake_status === 'open' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{house.intake_status}</Badge><p className="text-xs text-muted-foreground mt-1">Intake</p></CardContent></Card>
      </div>

      {/* Property Info */}
      {editMode ? (
        <Card className="border-0 shadow-sm rounded-xl"><CardHeader className="pb-2"><CardTitle className="text-sm">Edit Property</CardTitle></CardHeader><CardContent className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
            <div className="space-y-1.5"><Label>Address</Label><Input value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})} /></div>
          </div>
          <div className="space-y-1.5"><Label>Description / Bio</Label><DictationInput value={editForm.description} onChange={(val) => setEditForm({...editForm, description: val})} rows={2} showCleanup={false} /></div>
          <div className="space-y-1.5"><Label>House Rules</Label><DictationInput value={editForm.rules} onChange={(val) => setEditForm({...editForm, rules: val})} rows={4} showCleanup={false} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Owner / Director</Label><Input value={editForm.owner_name} onChange={e => setEditForm({...editForm, owner_name: e.target.value})} /></div>
            <div className="space-y-1.5"><Label>House Manager</Label><Input value={editForm.operator_name} onChange={e => setEditForm({...editForm, operator_name: e.target.value})} /></div>
          </div>
          <div className="space-y-1.5"><Label>Owner Bio</Label><DictationInput value={editForm.owner_bio} onChange={(val) => setEditForm({...editForm, owner_bio: val})} rows={2} showCleanup={false} /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Phone</Label><Input value={editForm.operator_phone} onChange={e => setEditForm({...editForm, operator_phone: e.target.value})} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={editForm.operator_email} onChange={e => setEditForm({...editForm, operator_email: e.target.value})} /></div>
          </div>
        </CardContent></Card>
      ) : (
        <>
          {house.description && <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">About</p><p className="text-sm">{house.description}</p></CardContent></Card>}
          {house.rules && <Card className="border-0 shadow-sm rounded-xl"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BookOpen size={14} /> House Rules</CardTitle></CardHeader><CardContent><div className="p-3 rounded-lg bg-slate-50 whitespace-pre-wrap text-sm">{house.rules}</div></CardContent></Card>}
          {(house.owner_name || house.operator_name) && (
            <Card className="border-0 shadow-sm rounded-xl"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><User size={14} /> Who Runs This House</CardTitle></CardHeader><CardContent className="space-y-3">
              {house.owner_name && <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">Owner / Director</p><p className="text-sm font-medium">{house.owner_name}</p>{house.owner_bio && <p className="text-xs text-slate-600 mt-1">{house.owner_bio}</p>}</div>}
              {house.operator_name && <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">House Manager</p><p className="text-sm font-medium">{house.operator_name}</p><div className="flex gap-3 mt-1 text-xs text-muted-foreground">{house.operator_phone && <span className="flex items-center gap-1"><Phone size={10}/>{house.operator_phone}</span>}{house.operator_email && <span className="flex items-center gap-1"><Mail size={10}/>{house.operator_email}</span>}</div></div>}
            </CardContent></Card>
          )}
        </>
      )}

      {/* Residents */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Current Residents ({house.residents?.length || 0})</CardTitle>
          {isStaff && (house.beds_available ?? house.available_beds ?? 0) > 0 && (
            <Dialog open={addResOpen} onOpenChange={setAddResOpen}>
              <DialogTrigger asChild><Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-1"><UserPlus size={14} /> Add</Button></DialogTrigger>
              <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Add Resident</DialogTitle></DialogHeader>
                <div className="space-y-3 pt-2">
                  <div className="space-y-1.5"><Label>Participant</Label><Select value={resForm.user_id} onValueChange={v => setResForm({...resForm, user_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1.5"><Label>Move-in Date</Label><Input type="date" value={resForm.move_in_date} onChange={e => setResForm({...resForm, move_in_date: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label>Phase</Label><Select value={resForm.program_phase} onValueChange={v => setResForm({...resForm, program_phase: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="orientation">Orientation</SelectItem><SelectItem value="stabilization">Stabilization</SelectItem><SelectItem value="recovery">Recovery</SelectItem><SelectItem value="leadership">Leadership</SelectItem></SelectContent></Select></div>
                  <Button onClick={addResident} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Add Resident</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {house.residents?.length > 0 ? <div className="space-y-2">{house.residents.map(r => (
            <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-sm font-bold text-cyan-700">{r.user_name?.charAt(0)}</div>
                <div><p className="text-sm font-medium">{r.user_name}</p><p className="text-xs text-muted-foreground">Since {r.move_in_date}</p></div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={r.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{r.compliance_status || 'compliant'}</Badge>
                <Badge variant="outline" className="text-[10px]">{r.program_phase}</Badge>
                {isStaff && <Button variant="ghost" size="sm" className="text-xs text-red-500 h-7" onClick={() => exitResident(r.id)}>Exit</Button>}
              </div>
            </div>
          ))}</div> : <p className="text-sm text-muted-foreground text-center py-4">No active residents</p>}
        </CardContent>
      </Card>
    </div>
  );
}

/* =============================== RESIDENTS (uses main housing API filtered) =============================== */
function OSResidents({ api, isStaff }) {
  const [houses, setHouses] = useState([]);
  const [residents, setResidents] = useState([]);
  const [selectedResident, setSelectedResident] = useState(null);

  useEffect(() => {
    api.get(`/housing/entity/${ENTITY}/houses`).then(r => {
      setHouses(r.data);
      setResidents(r.data.flatMap(h => (h.residents || []).map(re => ({...re, house_name: h.name, house_id: h.id}))));
    }).catch(() => {});
  }, []);

  if (selectedResident) {
    return <OSResidentProfile api={api} residentId={selectedResident} isStaff={isStaff} onBack={() => setSelectedResident(null)} />;
  }

  return (
    <div className="space-y-4" data-testid="os-residents">
      <p className="text-sm text-muted-foreground">{residents.length} residents across {houses.length} properties</p>
      <div className="space-y-2">
        {residents.map(r => (
          <Card key={r.id} className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedResident(r.id)} data-testid={`os-resident-${r.id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center text-sm font-bold text-cyan-700">{r.user_name?.charAt(0)}</div>
                <div><p className="font-medium text-sm">{r.user_name}</p><p className="text-xs text-muted-foreground">{r.house_name} &middot; Since {r.move_in_date}</p></div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={r.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{r.compliance_status || 'compliant'}</Badge>
                <Badge variant="outline" className="text-xs">{r.program_phase}</Badge>
                <ChevronRight size={14} className="text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
        {residents.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No residents</div>}
      </div>
    </div>
  );
}

/* =============================== RESIDENT PROFILE (360° View) =============================== */
function OSResidentProfile({ api, residentId, isStaff, onBack }) {
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [noteOpen, setNoteOpen] = useState(false);
  const [drugTestOpen, setDrugTestOpen] = useState(false);
  const [curfewOpen, setCurfewOpen] = useState(false);
  const [moveOutOpen, setMoveOutOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({ content: '', note_type: 'general' });
  const [dtForm, setDtForm] = useState({ test_type: 'urine', result: 'pending', notes: '' });
  const [curfewForm, setCurfewForm] = useState({ date: '', curfew_time: '22:00', check_in_time: '', status: 'on_time', notes: '' });
  const [moveOutForm, setMoveOutForm] = useState({ target_move_out_date: '', housing_search_status: 'not_started', destination_type: '', notes: '' });
  const [moveOutPlan, setMoveOutPlan] = useState(null);

  const load = useCallback(() => {
    api.get(`/housing/resident/${residentId}/profile`).then(r => setProfile(r.data)).catch(() => {});
    api.get(`/housing/moveout-plans?resident_id=${residentId}`).then(r => {
      const plans = r.data.filter(p => p.status === 'active');
      if (plans.length > 0) setMoveOutPlan(plans[0]);
    }).catch(() => {});
  }, [api, residentId]);
  useEffect(() => { load(); }, [load]);

  const addCaseNote = async () => {
    if (!noteForm.content) return toast.error('Note content required');
    try {
      await api.post('/housing/case-notes', { resident_id: residentId, user_id: profile.user_id, house_id: profile.house_id, ...noteForm });
      toast.success('Note added'); setNoteOpen(false); setNoteForm({ content: '', note_type: 'general' }); load();
    } catch { toast.error('Failed'); }
  };

  const addDrugTest = async () => {
    try {
      await api.post('/housing/drug-tests', { house_id: profile.house_id, resident_id: residentId, user_id: profile.user_id, ...dtForm });
      toast.success('Drug test logged'); setDrugTestOpen(false); setDtForm({ test_type: 'urine', result: 'pending', notes: '' }); load();
    } catch { toast.error('Failed'); }
  };

  const addCurfewLog = async () => {
    try {
      await api.post('/housing/curfew-logs', { house_id: profile.house_id, resident_id: residentId, user_id: profile.user_id, ...curfewForm });
      toast.success('Curfew log added'); setCurfewOpen(false); setCurfewForm({ date: '', curfew_time: '22:00', check_in_time: '', status: 'on_time', notes: '' }); load();
    } catch { toast.error('Failed'); }
  };

  const createMoveOutPlan = async () => {
    if (!moveOutForm.target_move_out_date) return toast.error('Target date required');
    try {
      await api.post('/housing/moveout-plans', { resident_id: residentId, user_id: profile.user_id, house_id: profile.house_id, ...moveOutForm });
      toast.success('Move-out plan created'); setMoveOutOpen(false); load();
    } catch { toast.error('Failed'); }
  };

  const updateChecklistItem = async (idx, completed) => {
    if (!moveOutPlan) return;
    const newChecklist = [...moveOutPlan.checklist];
    newChecklist[idx].completed = completed;
    try {
      await api.put(`/housing/moveout-plans/${moveOutPlan.id}`, { checklist: newChecklist });
      setMoveOutPlan({ ...moveOutPlan, checklist: newChecklist });
    } catch { toast.error('Failed'); }
  };

  const updatePhase = async (phase) => {
    try { await api.put(`/housing/resident/${residentId}/phase?phase=${phase}`); toast.success('Phase updated'); load(); } catch { toast.error('Failed'); }
  };

  if (!profile) return <div className="text-center py-12 text-muted-foreground">Loading profile...</div>;

  // Calculate stats
  const daysSinceMoveIn = profile.move_in_date ? Math.floor((new Date() - new Date(profile.move_in_date)) / (1000*60*60*24)) : 0;
  const curfewOnTime = profile.curfew_logs?.filter(c => c.status === 'on_time').length || 0;
  const curfewTotal = profile.curfew_logs?.length || 0;
  const curfewRate = curfewTotal > 0 ? Math.round((curfewOnTime / curfewTotal) * 100) : 100;
  const drugTestsPassed = profile.drug_tests?.filter(d => d.result === 'negative').length || 0;
  const drugTestsTotal = profile.drug_tests?.length || 0;
  const goalsCompleted = profile.goals?.filter(g => g.status === 'completed').length || 0;
  const goalsTotal = profile.goals?.length || 0;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'checkins', label: 'Check-ins', icon: Activity },
    { id: 'curfew', label: 'Curfew', icon: Moon },
    { id: 'drugtests', label: 'Drug Tests', icon: FlaskConical },
    { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
    { id: 'notes', label: 'Case Notes', icon: FileText },
    { id: 'goals', label: 'Goals', icon: ListChecks },
    { id: 'moveout', label: 'Move-Out Plan', icon: Home },
  ];

  return (
    <div className="space-y-5" data-testid="os-resident-profile">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="rounded-full" data-testid="back-to-residents">&larr; Back</Button>
          <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center text-lg font-bold text-cyan-700">{profile.user_name?.charAt(0)}</div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{profile.user_name}</h2>
            <p className="text-xs text-muted-foreground">Resident for {daysSinceMoveIn} days &middot; Move-in: {profile.move_in_date}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={profile.compliance_status === 'compliant' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{profile.compliance_status || 'compliant'}</Badge>
          <Badge variant="outline">{profile.program_phase}</Badge>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-cyan-700">{daysSinceMoveIn}</p>
          <p className="text-xs text-muted-foreground">Days in Program</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{curfewRate}%</p>
          <p className="text-xs text-muted-foreground">Curfew Compliance</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{drugTestsPassed}/{drugTestsTotal}</p>
          <p className="text-xs text-muted-foreground">Drug Tests Passed</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-purple-600">{goalsCompleted}/{goalsTotal}</p>
          <p className="text-xs text-muted-foreground">Goals Completed</p>
        </CardContent></Card>
      </div>

      {/* Action Buttons */}
      {isStaff && (
        <div className="flex gap-2 flex-wrap">
          <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1"><FileText size={14} /> Add Note</Button></DialogTrigger>
            <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Add Case Note</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5"><Label>Type</Label><Select value={noteForm.note_type} onValueChange={v => setNoteForm({...noteForm, note_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="staff_observation">Staff Observation</SelectItem><SelectItem value="progress_update">Progress Update</SelectItem><SelectItem value="warning">Warning</SelectItem></SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Note</Label><DictationInput value={noteForm.content} onChange={(val) => setNoteForm({...noteForm, content: val})} rows={4} showCleanup={false} /></div>
                <Button onClick={addCaseNote} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Save Note</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={drugTestOpen} onOpenChange={setDrugTestOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1"><FlaskConical size={14} /> Drug Test</Button></DialogTrigger>
            <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Log Drug Test</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Type</Label><Select value={dtForm.test_type} onValueChange={v => setDtForm({...dtForm, test_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="urine">Urine</SelectItem><SelectItem value="saliva">Saliva</SelectItem><SelectItem value="hair">Hair</SelectItem></SelectContent></Select></div>
                  <div className="space-y-1.5"><Label>Result</Label><Select value={dtForm.result} onValueChange={v => setDtForm({...dtForm, result: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="negative">Negative (Pass)</SelectItem><SelectItem value="positive">Positive (Fail)</SelectItem><SelectItem value="pending">Pending</SelectItem></SelectContent></Select></div>
                </div>
                <div className="space-y-1.5"><Label>Notes</Label><DictationInput value={dtForm.notes} onChange={(val) => setDtForm({...dtForm, notes: val})} rows={2} showCleanup={false} /></div>
                <Button onClick={addDrugTest} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Log Test</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={curfewOpen} onOpenChange={setCurfewOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1"><Moon size={14} /> Curfew Log</Button></DialogTrigger>
            <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Log Curfew Check</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={curfewForm.date} onChange={e => setCurfewForm({...curfewForm, date: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label>Status</Label><Select value={curfewForm.status} onValueChange={v => setCurfewForm({...curfewForm, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="on_time">On Time</SelectItem><SelectItem value="late">Late</SelectItem><SelectItem value="missed">Missed</SelectItem><SelectItem value="excused">Excused</SelectItem></SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Curfew Time</Label><Input type="time" value={curfewForm.curfew_time} onChange={e => setCurfewForm({...curfewForm, curfew_time: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label>Check-in Time</Label><Input type="time" value={curfewForm.check_in_time} onChange={e => setCurfewForm({...curfewForm, check_in_time: e.target.value})} /></div>
                </div>
                <Button onClick={addCurfewLog} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Log Curfew</Button>
              </div>
            </DialogContent>
          </Dialog>
          {!moveOutPlan && (
            <Dialog open={moveOutOpen} onOpenChange={setMoveOutOpen}>
              <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1"><Home size={14} /> Create Move-Out Plan</Button></DialogTrigger>
              <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Create Move-Out Plan</DialogTitle></DialogHeader>
                <div className="space-y-3 pt-2">
                  <div className="space-y-1.5"><Label>Target Move-Out Date</Label><Input type="date" value={moveOutForm.target_move_out_date} onChange={e => setMoveOutForm({...moveOutForm, target_move_out_date: e.target.value})} /></div>
                  <div className="space-y-1.5"><Label>Housing Search Status</Label><Select value={moveOutForm.housing_search_status} onValueChange={v => setMoveOutForm({...moveOutForm, housing_search_status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="not_started">Not Started</SelectItem><SelectItem value="searching">Searching</SelectItem><SelectItem value="applied">Applied</SelectItem><SelectItem value="secured">Secured</SelectItem></SelectContent></Select></div>
                  <div className="space-y-1.5"><Label>Destination Type</Label><Select value={moveOutForm.destination_type || '__none__'} onValueChange={v => setMoveOutForm({...moveOutForm, destination_type: v === '__none__' ? '' : v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__none__">Unknown</SelectItem><SelectItem value="independent_housing">Independent Housing</SelectItem><SelectItem value="sober_living">Sober Living</SelectItem><SelectItem value="family">Family</SelectItem><SelectItem value="shelter">Shelter</SelectItem></SelectContent></Select></div>
                  <div className="space-y-1.5"><Label>Notes</Label><DictationInput value={moveOutForm.notes} onChange={(val) => setMoveOutForm({...moveOutForm, notes: val})} rows={2} showCleanup={false} /></div>
                  <Button onClick={createMoveOutPlan} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Create Plan</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      )}

      {/* Program Phase */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity size={14} /> Program Phase</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {['orientation', 'stabilization', 'recovery', 'leadership'].map(p => (
              <Button key={p} size="sm" variant={profile.program_phase === p ? 'default' : 'outline'}
                className={`rounded-full text-xs capitalize ${profile.program_phase === p ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
                onClick={() => isStaff && updatePhase(p)}
              >{p}</Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap border-b pb-2">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeTab === t.id ? 'bg-cyan-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm rounded-xl"><CardHeader className="pb-2"><CardTitle className="text-sm">Resident Info</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">House</span><span className="font-medium">{profile.house_name || 'N/A'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Room / Bed</span><span className="font-medium">{profile.room_label && profile.bed_label ? `${profile.room_label} / ${profile.bed_label}` : 'Unassigned'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Move-in Date</span><span className="font-medium">{profile.move_in_date || 'N/A'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Expected Exit</span><span className="font-medium">{profile.expected_exit_date || 'TBD'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Rent Amount</span><span className="font-medium">{profile.rent_amount > 0 ? `$${profile.rent_amount}/mo` : 'Program-funded'}</span></div>
          </CardContent></Card>
          {profile.user_data && (
            <Card className="border-0 shadow-sm rounded-xl"><CardHeader className="pb-2"><CardTitle className="text-sm">Contact Info</CardTitle></CardHeader><CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="font-medium">{profile.user_data.email}</span></div>
              {profile.user_data.phone && <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span className="font-medium">{profile.user_data.phone}</span></div>}
            </CardContent></Card>
          )}
        </div>
      )}

      {activeTab === 'checkins' && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Check-ins (Peer Support)</CardTitle></CardHeader>
          <CardContent>
            {profile.recent_checkins?.length > 0 ? (
              <div className="space-y-2">{profile.recent_checkins.map((c, i) => (
                <div key={`item-${i}`} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="text-center"><p className="text-lg font-bold text-cyan-700">{c.mood}</p><p className="text-[10px] text-muted-foreground">/10</p></div>
                    <div><p className="text-sm font-medium">{new Date(c.date || c.created_at).toLocaleDateString()}</p>{c.wins && <p className="text-xs text-emerald-600 mt-0.5">Win: {c.wins}</p>}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.stayed_sober !== undefined && <Badge className={c.stayed_sober ? 'bg-emerald-50 text-emerald-700 border-0 text-xs' : 'bg-red-50 text-red-700 border-0 text-xs'}>{c.stayed_sober ? 'Sober' : 'Relapse'}</Badge>}
                    {c.energy && <Badge variant="outline" className="text-xs">Energy: {c.energy}/10</Badge>}
                  </div>
                </div>
              ))}</div>
            ) : <p className="text-sm text-muted-foreground text-center py-4">No check-ins recorded</p>}
          </CardContent>
        </Card>
      )}

      {activeTab === 'curfew' && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2 flex flex-row items-center justify-between"><CardTitle className="text-sm">Curfew Log ({curfewRate}% On-Time)</CardTitle></CardHeader>
          <CardContent>
            {profile.curfew_logs?.length > 0 ? (
              <div className="space-y-2">{profile.curfew_logs.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div><p className="text-sm font-medium">{c.date}</p><p className="text-xs text-muted-foreground">Curfew: {c.curfew_time} {c.check_in_time && `• In: ${c.check_in_time}`}</p></div>
                  <Badge className={c.status === 'on_time' ? 'bg-emerald-50 text-emerald-700 border-0' : c.status === 'excused' ? 'bg-blue-50 text-blue-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{c.status?.replace(/_/g, ' ')}</Badge>
                </div>
              ))}</div>
            ) : <p className="text-sm text-muted-foreground text-center py-4">No curfew logs</p>}
          </CardContent>
        </Card>
      )}

      {activeTab === 'drugtests' && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Drug Test History ({drugTestsPassed}/{drugTestsTotal} Passed)</CardTitle></CardHeader>
          <CardContent>
            {profile.drug_tests?.length > 0 ? (
              <div className="space-y-2">{profile.drug_tests.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div><p className="text-sm font-medium">{new Date(d.created_at).toLocaleDateString()}</p><p className="text-xs text-muted-foreground capitalize">{d.test_type} test</p></div>
                  <Badge className={d.result === 'negative' ? 'bg-emerald-50 text-emerald-700 border-0' : d.result === 'positive' ? 'bg-red-50 text-red-700 border-0' : 'bg-amber-50 text-amber-700 border-0'}>{d.result === 'negative' ? 'Pass' : d.result === 'positive' ? 'Fail' : d.result}</Badge>
                </div>
              ))}</div>
            ) : <p className="text-sm text-muted-foreground text-center py-4">No drug tests recorded</p>}
          </CardContent>
        </Card>
      )}

      {activeTab === 'incidents' && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Incident History ({profile.incidents?.length || 0})</CardTitle></CardHeader>
          <CardContent>
            {profile.incidents?.length > 0 ? (
              <div className="space-y-2">{profile.incidents.map(i => (
                <div key={i.id} className="p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={i.severity === 'critical' ? 'bg-red-100 text-red-800 border-0' : i.severity === 'high' ? 'bg-orange-100 text-orange-800 border-0' : 'bg-slate-100 text-slate-700 border-0'}>{i.severity}</Badge>
                    <Badge variant="outline" className="text-xs">{i.incident_type?.replace(/_/g, ' ')}</Badge>
                    <span className="text-xs text-muted-foreground ml-auto">{new Date(i.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm">{i.description}</p>
                  {i.action_taken && <p className="text-xs text-cyan-700 mt-1">Action: {i.action_taken}</p>}
                </div>
              ))}</div>
            ) : <p className="text-sm text-muted-foreground text-center py-4">No incidents recorded</p>}
          </CardContent>
        </Card>
      )}

      {activeTab === 'notes' && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Case Notes ({profile.case_notes?.length || 0})</CardTitle></CardHeader>
          <CardContent>
            {profile.case_notes?.length > 0 ? (
              <div className="space-y-2">{profile.case_notes.map(n => (
                <div key={n.id} className="p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">{n.note_type?.replace(/_/g, ' ')}</Badge>
                    <span className="text-xs text-muted-foreground">by {n.created_by_name}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{new Date(n.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm">{n.content}</p>
                </div>
              ))}</div>
            ) : <p className="text-sm text-muted-foreground text-center py-4">No case notes</p>}
          </CardContent>
        </Card>
      )}

      {activeTab === 'goals' && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Recovery Goals ({goalsCompleted}/{goalsTotal} Completed)</CardTitle></CardHeader>
          <CardContent>
            {profile.goals?.length > 0 ? (
              <div className="space-y-2">{profile.goals.map(g => (
                <div key={g.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    {g.status === 'completed' ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Activity size={16} className="text-amber-500" />}
                    <div><p className="text-sm font-medium">{g.title}</p>{g.category && <p className="text-xs text-muted-foreground capitalize">{g.category}</p>}</div>
                  </div>
                  <Badge variant="outline" className="text-xs">{g.status}</Badge>
                </div>
              ))}</div>
            ) : <p className="text-sm text-muted-foreground text-center py-4">No goals set</p>}
          </CardContent>
        </Card>
      )}

      {activeTab === 'moveout' && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Home size={14} /> Move-Out Planning</CardTitle></CardHeader>
          <CardContent>
            {moveOutPlan ? (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">Target Date</p><p className="text-sm font-medium">{moveOutPlan.target_move_out_date || 'TBD'}</p></div>
                  <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">Housing Search</p><Badge className="mt-1" variant="outline">{moveOutPlan.housing_search_status?.replace(/_/g, ' ')}</Badge></div>
                  <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">Destination</p><p className="text-sm font-medium capitalize">{moveOutPlan.destination_type?.replace(/_/g, ' ') || 'TBD'}</p></div>
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">Move-Out Checklist</p>
                  <div className="space-y-2">
                    {moveOutPlan.checklist?.map((item, idx) => (
                      <div key={`item-${idx}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                        <input type="checkbox" checked={item.completed} onChange={(e) => isStaff && updateChecklistItem(idx, e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500" disabled={!isStaff} />
                        <span className={`text-sm ${item.completed ? 'line-through text-muted-foreground' : ''}`}>{item.item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {moveOutPlan.notes && <div className="p-3 rounded-lg bg-slate-50"><p className="text-xs text-muted-foreground">Notes</p><p className="text-sm">{moveOutPlan.notes}</p></div>}
              </div>
            ) : (
              <div className="text-center py-6">
                <Home size={32} className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-muted-foreground">No move-out plan created yet</p>
                {isStaff && <p className="text-xs text-cyan-600 mt-1">Click "Create Move-Out Plan" above to start</p>}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* =============================== MEETINGS =============================== */
function OSMeetings({ api, isStaff }) {
  const [meetings, setMeetings] = useState([]);
  const [houses, setHouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ house_id: '', meeting_type: 'weekly', date: '', time: '', topics: '', notes: '', attendee_ids: [] });

  useEffect(() => {
    api.get(`/housing/entity/${ENTITY}/houses`).then(r => setHouses(r.data)).catch(() => {});
    api.get('/housing/meetings').then(r => setMeetings(r.data)).catch(() => {});
  }, []);

  const houseIds = houses.map(h => h.id);
  const filtered = meetings.filter(m => houseIds.includes(m.house_id));

  const create = async () => {
    if (!form.house_id) return toast.error('Select house');
    try { await api.post('/housing/meetings', {...form, topics: form.topics.split(',').map(t => t.trim()).filter(Boolean)}); toast.success('Meeting saved'); setOpen(false); api.get('/housing/meetings').then(r => setMeetings(r.data)); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="os-meetings">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{filtered.length} meetings</p>
        {isStaff && <Dialog open={open} onOpenChange={setOpen}><DialogTrigger asChild><Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2"><Plus size={16} /> Document Meeting</Button></DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg"><DialogHeader><DialogTitle>Document Meeting</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5"><Label>House</Label><Select value={form.house_id} onValueChange={v => setForm({...form, house_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3"><div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div><div className="space-y-1.5"><Label>Time</Label><Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} /></div></div>
              <div className="space-y-1.5"><Label>Topics</Label><Input value={form.topics} onChange={e => setForm({...form, topics: e.target.value})} placeholder="Comma separated" /></div>
              <div className="space-y-1.5"><Label>Notes</Label><DictationInput value={form.notes} onChange={(val) => setForm({...form, notes: val})} rows={3} showCleanup={false} /></div>
              <Button onClick={create} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Save Meeting</Button>
            </div>
          </DialogContent></Dialog>}
      </div>
      {filtered.map(m => { const house = houses.find(h => h.id === m.house_id); return (
        <Card key={m.id} className="border-0 shadow-sm rounded-xl"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-1"><Badge variant="outline" className="text-xs">{m.meeting_type?.replace(/_/g,' ')}</Badge><span className="text-xs text-muted-foreground">{m.date} {m.time}</span></div>
          <p className="text-sm font-medium">{house?.name}</p>
          {m.notes && <p className="text-xs text-slate-600 mt-1">{m.notes}</p>}
        </CardContent></Card>
      ); })}
      {filtered.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No meetings</div>}
    </div>
  );
}

/* =============================== INCIDENTS =============================== */
function OSIncidents({ api, isStaff }) {
  const [incidents, setIncidents] = useState([]);
  const [houses, setHouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ house_id: '', incident_type: 'rule_violation', severity: 'low', description: '', action_taken: '' });

  useEffect(() => {
    api.get(`/housing/entity/${ENTITY}/houses`).then(r => setHouses(r.data)).catch(() => {});
    api.get('/housing/incidents').then(r => setIncidents(r.data)).catch(() => {});
  }, []);

  const houseIds = houses.map(h => h.id);
  const filtered = incidents.filter(i => houseIds.includes(i.house_id));

  const create = async () => {
    if (!form.house_id || !form.description) return toast.error('House and description required');
    try { await api.post('/housing/incidents', form); toast.success('Reported'); setOpen(false); api.get('/housing/incidents').then(r => setIncidents(r.data)); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="os-incidents">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{filtered.length} incidents</p>
        {isStaff && <Dialog open={open} onOpenChange={setOpen}><DialogTrigger asChild><Button className="bg-red-500 hover:bg-red-600 text-white rounded-full gap-2"><AlertTriangle size={16} /> Report</Button></DialogTrigger>
          <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Report Incident</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5"><Label>House</Label><Select value={form.house_id} onValueChange={v => setForm({...form, house_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Type</Label><Select value={form.incident_type} onValueChange={v => setForm({...form, incident_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="rule_violation">Rule Violation</SelectItem><SelectItem value="curfew_violation">Curfew</SelectItem><SelectItem value="behavioral">Behavioral</SelectItem><SelectItem value="substance_use">Substance Use</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
                <div className="space-y-1.5"><Label>Severity</Label><Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent></Select></div>
              </div>
              <div className="space-y-1.5"><Label>Description</Label><DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} rows={3} showCleanup={false} /></div>
              <Button onClick={create} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full">Report</Button>
            </div>
          </DialogContent></Dialog>}
      </div>
      {filtered.map(i => (<Card key={i.id} className="border-0 shadow-sm rounded-xl"><CardContent className="p-4"><div className="flex items-center gap-2 mb-1"><Badge className={i.severity === 'critical' ? 'bg-red-100 text-red-800 border-0' : i.severity === 'high' ? 'bg-orange-100 text-orange-800 border-0' : 'bg-slate-100 text-slate-700 border-0'}>{i.severity}</Badge><Badge variant="outline" className="text-xs">{i.incident_type?.replace(/_/g,' ')}</Badge><span className="text-xs text-muted-foreground ml-auto">{new Date(i.created_at).toLocaleDateString()}</span></div><p className="text-sm">{i.description}</p></CardContent></Card>))}
      {filtered.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No incidents</div>}
    </div>
  );
}

/* =============================== WAITLIST =============================== */
function OSWaitlist({ api, isStaff }) {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [houses, setHouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: '', preferred_house_id: '', priority: 'normal', notes: '' });

  const load = useCallback(() => {
    api.get('/housing/waitlist').then(r => setEntries(r.data)).catch(() => {});
    api.get(`/housing/entity/${ENTITY}/houses`).then(r => setHouses(r.data)).catch(() => {});
    if (isStaff) api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
  }, [api, isStaff]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.user_id) return toast.error('Select participant');
    try { await api.post('/housing/waitlist', { ...form, entity: ENTITY }); toast.success('Added'); setOpen(false); load(); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const priorityColor = { urgent: 'bg-red-100 text-red-800', high: 'bg-orange-100 text-orange-800', normal: 'bg-blue-100 text-blue-800', low: 'bg-slate-100 text-slate-700' };

  return (
    <div className="space-y-4" data-testid="os-waitlist">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{entries.length} on waitlist</p>
        {isStaff && <Dialog open={open} onOpenChange={setOpen}><DialogTrigger asChild><Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2"><Plus size={16} /> Add</Button></DialogTrigger>
          <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Add to Waitlist</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5"><Label>Participant</Label><Select value={form.user_id} onValueChange={v => setForm({...form, user_id: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Preferred House</Label><Select value={form.preferred_house_id || '__any__'} onValueChange={v => setForm({...form, preferred_house_id: v === '__any__' ? '' : v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="__any__">Any</SelectItem>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Priority</Label><Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select></div>
              <Button onClick={add} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Add to Waitlist</Button>
            </div></DialogContent></Dialog>}
      </div>
      {entries.map((e, i) => (<Card key={e.id} className="border-0 shadow-sm rounded-xl"><CardContent className="p-4 flex items-center justify-between"><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-sm font-bold text-purple-700">#{i+1}</div><div><p className="text-sm font-medium">{e.user_name}</p><p className="text-xs text-muted-foreground">Added {new Date(e.created_at).toLocaleDateString()}</p></div></div><Badge className={`${priorityColor[e.priority] || priorityColor.normal} border-0 text-xs`}>{e.priority}</Badge></CardContent></Card>))}
      {entries.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">Empty</div>}
    </div>
  );
}

/* =============================== RENT TRACKER =============================== */
function OSRentTracker({ api, isStaff, isCoordinator }) {
  const [houses, setHouses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ house_id: '', resident_id: '', user_id: '', user_name: '', amount: 0, period: '', payment_method: 'cash', notes: '' });

  const load = useCallback(() => {
    api.get(`/housing/entity/${ENTITY}/houses`).then(r => setHouses(r.data)).catch(() => {});
    api.get('/housing/rent-payments').then(r => setPayments(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const houseIds = houses.map(h => h.id);
  const filtered = payments.filter(p => houseIds.includes(p.house_id));
  const residents = houses.flatMap(h => (h.residents || []).map(r => ({...r, house_name: h.name, house_id: h.id})));

  const create = async () => {
    if (!form.resident_id || !form.amount) return toast.error('Select resident and amount');
    try { await api.post('/housing/rent-payments', form); toast.success('Payment recorded'); setOpen(false); load(); } catch { toast.error('Failed'); }
  };

  const totalCollected = filtered.reduce((s, p) => s + (p.status === 'paid' ? p.amount : 0), 0);

  return (
    <div className="space-y-4" data-testid="os-rent">
      <div className="flex items-center justify-between">
        <div><p className="text-sm text-muted-foreground">{filtered.length} payments</p><p className="text-lg font-bold text-emerald-700">${totalCollected} collected</p></div>
        {isCoordinator && <Dialog open={open} onOpenChange={setOpen}><DialogTrigger asChild><Button className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2"><Plus size={16} /> Record Payment</Button></DialogTrigger>
          <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Record Rent Payment</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5"><Label>Resident</Label><Select value={form.resident_id || ''} onValueChange={v => { const res = residents.find(r => r.id === v); setForm({...form, resident_id: v, user_id: res?.user_id || '', user_name: res?.user_name || '', house_id: res?.house_id || ''}); }}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{residents.map(r => <SelectItem key={r.id} value={r.id}>{r.user_name} ({r.house_name})</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Amount ($)</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} /></div>
                <div className="space-y-1.5"><Label>Period</Label><Input type="month" value={form.period} onChange={e => setForm({...form, period: e.target.value})} /></div>
              </div>
              <div className="space-y-1.5"><Label>Method</Label><Select value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="check">Check</SelectItem><SelectItem value="money_order">Money Order</SelectItem><SelectItem value="electronic">Electronic</SelectItem></SelectContent></Select></div>
              <Button onClick={create} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Record Payment</Button>
            </div></DialogContent></Dialog>}
      </div>
      {filtered.map(p => (<Card key={p.id} className="border-0 shadow-sm rounded-xl"><CardContent className="p-4 flex items-center justify-between"><div><p className="text-sm font-medium">{p.user_name}</p><p className="text-xs text-muted-foreground">{p.period} &middot; {p.payment_method}</p></div><div className="flex items-center gap-2"><span className="text-lg font-bold text-emerald-700">${p.amount}</span><Badge className={p.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-red-50 text-red-700 border-0'}>{p.status}</Badge></div></CardContent></Card>))}
      {filtered.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No payments recorded</div>}
    </div>
  );
}

/* =============================== REPORTS =============================== */
function OSReports({ api }) {
  const [houses, setHouses] = useState([]);
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState('');

  useEffect(() => { api.get(`/housing/entity/${ENTITY}/houses`).then(r => setHouses(r.data)).catch(() => {}); }, []);

  const gen = async () => {
    if (!selected) return toast.error('Select house');
    try { const r = await api.get(`/housing/reports/house/${selected}`); setReport(r.data); } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="os-reports">
      <Card className="border-0 shadow-sm rounded-xl"><CardContent className="p-4 space-y-3">
        <Select value={selected} onValueChange={setSelected}><SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger><SelectContent>{houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent></Select>
        <Button onClick={gen} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">Generate Report</Button>
      </CardContent></Card>
      {report && <Card className="border-0 shadow-sm rounded-xl"><CardHeader><CardTitle className="text-base">{report.house?.name} — Report</CardTitle></CardHeader><CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center p-3 rounded-lg bg-slate-50"><p className="text-xl font-bold">{report.total_residents}</p><p className="text-xs text-muted-foreground">Residents</p></div>
          <div className="text-center p-3 rounded-lg bg-emerald-50"><p className="text-xl font-bold text-emerald-700">{report.compliant_residents}</p><p className="text-xs text-muted-foreground">Compliant</p></div>
          <div className="text-center p-3 rounded-lg bg-red-50"><p className="text-xl font-bold text-red-700">{report.non_compliant_residents}</p><p className="text-xs text-muted-foreground">Non-Compliant</p></div>
          <div className="text-center p-3 rounded-lg bg-blue-50"><p className="text-xl font-bold text-blue-700">{report.occupancy_rate}%</p><p className="text-xs text-muted-foreground">Occupancy</p></div>
        </div>
      </CardContent></Card>}
    </div>
  );
}

/* =============================== REFERRALS =============================== */
function OSReferrals({ api }) {
  const [referrals, setReferrals] = useState([]);
  useEffect(() => { api.get('/referrals').then(r => setReferrals(r.data.filter(ref => ref.referring_to === 'our_square' || ref.referring_to === 'both'))).catch(() => {}); }, []);

  return (
    <div className="space-y-4" data-testid="os-referrals">
      <p className="text-sm text-muted-foreground">{referrals.length} referrals to Our Square</p>
      {referrals.map(r => (<Card key={r.id} className="border-0 shadow-sm rounded-xl"><CardContent className="p-4">
        <div className="flex items-center justify-between"><p className="text-sm font-medium">{r.participant_name || r.name || 'Participant'}</p><Badge className={r.status === 'accepted' ? 'bg-emerald-50 text-emerald-700 border-0' : r.status === 'pending' ? 'bg-amber-50 text-amber-700 border-0' : 'bg-slate-50 text-slate-700 border-0'}>{r.status}</Badge></div>
        <p className="text-xs text-muted-foreground mt-1">By {r.referred_by_name} &middot; {new Date(r.created_at).toLocaleDateString()}</p>
        {r.notes && <p className="text-xs text-slate-600 mt-1">{r.notes}</p>}
      </CardContent></Card>))}
      {referrals.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">No referrals to Our Square yet</div>}
    </div>
  );
}
